import React, { useEffect, useState, useCallback } from 'react';

import CustomTripBox from './CustomTripBox';
import Sidebar from '../../components/Sidebar';
import GreenLoader from '../../assets/greenLoader.gif';

import { MainBox, UserName } from '../Elements';
import { FormBox, CountName } from './Elements';
import { useMediaQuery, Box, Button, TextField } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { getFunction } from '../../features/auth/action';
import { responseMessages } from '../../data';
import backend from '../../api';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles({
  root: {
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      borderColor: '#D8E1DB',
      borderRadius: '100px',
      boxShadow: 'inset 1px 1px 1px rgba(0, 0, 0, 0.2)',
      backgroundColor: 'white',
    },
    '&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      borderColor: '#1E3625',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#3C6B49',
    },
    '& .MuiOutlinedInput-input': {
      padding: '10px',
      color: '#1E3625',
      zIndex: 20,
      '&::placeholder': {
        zIndex: 20,
        fontSize: 'small',
        fontFamily: 'Poppins-Regular',
        '@media(max-width: 2000px)': {
          fontSize: 'medium',
        },
      },
    },
    '&:hover .MuiOutlinedInput-input': {
      color: '#1E3625',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-input': {
      color: '#1E3625',
    },
    '& .MuiInputLabel-outlined': {
      color: 'green',
    },
    '&:hover .MuiInputLabel-outlined': {
      color: '#1E3625',
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#1E3625',
    },
  },
});

// Custom hook for debounce functionality
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const SavedTrips = () => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const [savedTrips, setSavedTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 500);

  const { userInfo, isAuth } = useSelector((state) => state.auth);

  const matches600 = useMediaQuery('(max-width:600px)');

  //   const updateUser = () => {
  //     // let body = { name: 'userInfo', url: `/user/getUser/${userInfo?._id}` }
  //     let body = { name: 'savedTrips', url: `/savetrips?userId=${userInfo?._id}` };
  //     dispatch(getFunction(body));
  //   };

  const getSavedTrips = useCallback(async (params) => {
    try {
      setLoading(true);
      const { data } = await backend.get(`/savetrips`, { params });
      if (params.page === 1) {
        setSavedTrips(data.data?.filter((trip) => trip.tripId) || []);
      } else {
        setSavedTrips((prev) => [...prev, ...(data.data?.filter((trip) => trip.tripId) || [])]);
      }
      setTotalPages(data.total_pages);
      setTotalCount(data.total_count);
    } catch (error) {
      console.log(error);
      // Handle the "No More Record Found" case gracefully
      if (error.response && error.response.status === 404) {
        if (params.page > 1) {
          // We've reached the end of the data during pagination
          setTotalPages(params.page - 1);
        } else {
          // No records found for the initial search
          setSavedTrips([]);
          setTotalPages(0);
          setTotalCount(0);
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Separate function for refreshing the trips list
  const refreshSavedTrips = useCallback(() => {
    setPage(1);
    getSavedTrips({ 
      userId: userInfo?._id, 
      page: 1, 
      limit, 
      search: debouncedSearch 
    });
  }, [userInfo?._id, limit, debouncedSearch, getSavedTrips]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
    setPage(1); // Reset page when search changes
  };

  // Handle load more button click
  const handleLoadMore = () => {
    setPage((prevPage) => prevPage + 1);
  };

  // Effect for debounced search
  useEffect(() => {
    if (isAuth) {
      refreshSavedTrips();
    }
    // eslint-disable-next-line
  }, [debouncedSearch, userInfo?._id, limit]);

  // Effect for pagination
  useEffect(() => {
    if (isAuth && page > 1) {
      getSavedTrips({ userId: userInfo?._id, page, limit, search: debouncedSearch });
    }
    // eslint-disable-next-line
  }, [page]);

  const trips = savedTrips?.filter((trip) => trip.tripId) || [];
  const hasMorePages = page < totalPages;

  return (
    <MainBox sx={{ marginTop: matches600 ? 6 : 0 }}>
      <Sidebar />
      <FormBox>
        <UserName sx={{ fontSize: '18px' }}>Saved Trips</UserName>

        {/* Search input */}
        <Box sx={{ mb: 2, mt: 2 }}>
          <TextField
            className={classes.root}
            fullWidth
            variant="outlined"
            placeholder="Search trips..."
            value={searchInput}
            onChange={handleSearchChange}
            size="small"
          />
        </Box>

        {loading && page === 1 && (
          <div
            style={{
              position: 'absolute',
              marginLeft: 'auto',
              marginRight: 'auto',
              textAlign: 'center',
              left: '50%',
              top: '50%',
            }}
          >
            <img src={GreenLoader} width="80" alt="GreenLoader" />
          </div>
        )}
        {trips?.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '94vh',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CountName>{responseMessages.noSavedTrips}</CountName>
          </Box>
        ) : (
          <>
            <CountName sx={{ mt: 2, mb: 1 }}>You have {totalCount} trip(s) saved</CountName>
            {trips?.map((res, i) => {
              if (res?.tripId != null) {
                return (
                  <CustomTripBox
                    updateUser={refreshSavedTrips}
                    res={res?.trip}
                    key={i}
                    data={res}
                  />
                );
              }
              return null;
            })}

            {/* Loading indicator for load more */}
            {loading && page > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <img src={GreenLoader} width="50" alt="GreenLoader" />
              </Box>
            )}

            {/* Load more button */}
            {hasMorePages && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleLoadMore}
                  disabled={loading}
                >
                  Load More
                </Button>
              </Box>
            )}
          </>
        )}
      </FormBox>
    </MainBox>
  );
};
export default SavedTrips;
